import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { X, Check, Info, Link, LockKeyhole, Globe } from "lucide-react";
import { listCWUsers, listUsersByProject, sendEmailToOwner, updateAccess } from "@/core/api/api";
import useAuthStore from "@/shared/zustand/authStore";
import { Skeleton } from "primereact/skeleton";
import useToastStore from "@/shared/zustand/toastStore";
import { createFolderSharingTemplate } from "@/shared/utils/appLayoutUtils/appLayoutUtilities";
import { checkModeSpecificScopes } from "@/shared/utils/helpers";
const roleOptionsForExisting = [
    { label: "Editor", value: "editor" },
    { label: "Viewer", value: "viewer" },
    { label: "Revoke Access", value: "remove" },
];
// Role options for new users (no Revoke Access)
const roleOptionsForNewUsers = [
    { label: "Editor", value: "editor" },
    { label: "Viewer", value: "viewer" },
];
// general access options
const generalAccessOptions = [
    {
        label: "Restricted",
        value: "restricted",
        description: "Only people with access can open with the link",
    },
    {
        label: "Anyone with the link",
        value: "anyone",
        description: "Anyone with this link can view",
    },
];
const emailSendScope = "https://www.googleapis.com/auth/gmail.send";
// Enhanced Chips Component with Search
const SearchSelectChips = ({ selectedUsers = [], onUserSelect, onUserRemove, suggestions = [], className = "", selectedProjectToShare, placeholder = "Add someone by entering their name or email.", usersWithAccess }) => {
    const showToast = useToastStore((state) => state.showToast);
    const [inputValue, setInputValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const chipsContainerRef = useRef(null);
    const { user } = useAuthStore();
    // Filter out users that are already selected
    const availableSuggestions = suggestions.filter((suggestion) => !selectedUsers.some((user) => user.id === suggestion._id));
    // Filter suggestions based on input value
    useEffect(() => {
        if (inputValue.trim() === "") {
            setFilteredSuggestions(availableSuggestions.slice(0, 5)); // Show first 5 by default when empty
        }
        else {
            const filtered = availableSuggestions.filter((suggestion) => (suggestion.user_name &&
                suggestion.user_name
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())) ||
                (suggestion.email &&
                    suggestion.email.toLowerCase().includes(inputValue.toLowerCase())));
            setFilteredSuggestions(filtered.slice(0, 10)); // Limit to 10 results
        }
    }, [inputValue, suggestions, selectedUsers]);
    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };
    const handleKeyDown = (e) => {
        if (e.key === "Backspace" &&
            inputValue === "" &&
            selectedUsers.length > 0) {
            // Remove the last chip when pressing backspace with empty input
            onUserRemove(selectedUsers[selectedUsers.length - 1].id);
        }
    };
    const handleSelectUser = (user) => {
        const selectedUser = {
            id: user._id,
            name: user.user_name,
            email: user.email,
        };
        onUserSelect(selectedUser);
        setInputValue("");
        setIsFocused(false);
        // Focus back on input after selection
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 10);
    };
    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)) {
                setIsFocused(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    const handleInvite = async () => {
        try {
            setIsFocused(true);
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            // console.log("inputVal", inputValue," and emailRegex", emailRegex.test(inputValue));
            const res = emailRegex.test(inputValue);
            if (!res) {
                showToast("error", "Error", "Please enter a valid email address.");
                return;
            }
            else {
                if (!selectedProjectToShare) {
                    console.warn("No project to share");
                    return;
                }
                const customAccessUpdates = [];
                customAccessUpdates.push({
                    user_email: inputValue,
                    scope: "viewer",
                    user_name: inputValue,
                });
                const { responseData, error } = await updateAccess(selectedProjectToShare?._id, customAccessUpdates);
                if (error) {
                    console.error("Error updating access", error);
                    showToast("error", "Error", error.errorMessage);
                    return;
                }
                if (responseData) {
                    showToast("success", "Success", "Invited successfully");
                }
                const ownerUser = usersWithAccess.find((user) => user.role === "owner");
                if (!ownerUser) {
                    console.error("Owner user not found for this project", selectedProjectToShare);
                    showToast("error", "Error", "Owner user not found for this project");
                    return;
                }
                if (!user ||
                    !user.email ||
                    !user.provider.name ||
                    !user.provider.scope ||
                    !user.provider.access_token ||
                    !user.provider.refresh_token) {
                    showToast("error", "Error", "User details insufficient to notify people");
                    console.warn("User details insufficient to notify people", user);
                    return;
                }
                const projectURL = window.location.origin + `/chat?projectId=${selectedProjectToShare?._id}`;
                const emailBodyForSharedProject = createFolderSharingTemplate(ownerUser?.name, selectedProjectToShare.name, projectURL);
                const emailSubject = "Project Shared with you";
                const hasEmailSendScope = checkModeSpecificScopes(user.provider.scope, emailSendScope);
                if (!hasEmailSendScope) {
                    showToast("warn", "Warn", "Cant notify people, user does not have email send scope");
                    console.warn("Cant notify people, user does not have email send scope", user);
                    return;
                }
                // Send email notifications to all users who received access
                try {
                    const emailPromises = customAccessUpdates.map(async (accessUpdate) => {
                        try {
                            const { responseData: emailResponseData, error: emailError } = await sendEmailToOwner(ownerUser.email, // fromEmail (owner's email)
                            accessUpdate.user_email, // toEmail (recipient's email)
                            emailBodyForSharedProject, // email body
                            emailSubject, // email subject
                            user?.provider?.access_token, user?.provider?.refresh_token);
                            if (emailError) {
                                console.error(`Error sending email to ${accessUpdate.user_email}:`, emailError);
                                return {
                                    status: 'rejected',
                                    reason: emailError,
                                    email: accessUpdate.user_email
                                };
                            }
                            console.log(`Email sent successfully to ${accessUpdate.user_email}`);
                            return {
                                status: 'fulfilled',
                                value: emailResponseData,
                                email: accessUpdate.user_email
                            };
                        }
                        catch (err) {
                            console.error(`Exception sending email to ${accessUpdate.user_email}:`, err);
                            return {
                                status: 'rejected',
                                reason: err,
                                email: accessUpdate.user_email
                            };
                        }
                    });
                    // Execute all email sending promises in parallel
                    const emailResults = await Promise.allSettled(emailPromises);
                    // Log results
                    const successfulEmails = emailResults.filter(result => result.status === 'fulfilled').length;
                    const failedEmails = emailResults.filter(result => result.status === 'rejected').length;
                    console.log(`Email notifications: ${successfulEmails} sent successfully, ${failedEmails} failed`);
                    if (failedEmails > 0) {
                        showToast("warn", "Warn", `${successfulEmails} sent, ${failedEmails} failed.`);
                    }
                    else if (successfulEmails > 0) {
                        showToast("success", "Success", "Notifications sent successfully");
                    }
                }
                catch (emailErr) {
                    console.error("Error sending email notifications:", emailErr);
                    showToast("error", "Error", "Failed to send some email notifications");
                }
            }
        }
        catch (error) {
            console.error("Error inviting user:", error);
            showToast("error", "Error", "Error inviting user.");
        }
        finally {
            setIsFocused(false);
        }
    };
    return (_jsxs("div", { className: `relative ${className}`, children: [_jsxs("div", { ref: chipsContainerRef, className: "border border-[#d1d5db] p-2 rounded-[8px] flex flex-wrap items-center gap-2 min-h-[48px] max-h-[80px] overflow-y-auto", onClick: () => inputRef.current?.focus(), children: [selectedUsers.map((user) => (_jsxs("div", { className: " bg-gradient-to-r from-[#E9ECFD] to-[#EEE9FD] text-sm px-2 py-1 rounded-[6px] flex items-center gap-1 flex-shrink-0", children: [_jsx("span", { className: "text-[#1D2939]", children: user.name }), _jsx(Button, { type: "button", icon: _jsx(X, { size: 14 }), className: "p-0 text-gray-500 hover:text-gray-700", text: true, onClick: (e) => {
                                    e.stopPropagation();
                                    onUserRemove(user.id);
                                } })] }, user.id))), _jsx("div", { className: "flex-1 min-w-[120px] flex items-center", children: _jsx(InputText, { ref: inputRef, value: inputValue, onChange: handleInputChange, onKeyDown: handleKeyDown, onFocus: () => setIsFocused(true), onBlur: () => {
                                // Add slight delay before closing dropdown to allow for clicks
                                setTimeout(() => {
                                    if (document.activeElement !== inputRef.current) {
                                        setIsFocused(false);
                                    }
                                }, 200);
                            }, placeholder: selectedUsers.length === 0 ? placeholder : "", className: "border-none p-0 w-full focus:shadow-none" }) })] }), isFocused && filteredSuggestions.length > 0 && (_jsx("div", { ref: dropdownRef, className: `absolute z-10 mt-1 w-full bg-white border border-[#d1d5db] rounded-md shadow-lg max-h-60 overflow-auto`, children: filteredSuggestions.map((suggestion) => (_jsxs("div", { className: "flex items-center gap-3 p-2 hover:bg-gray-100 cursor-pointer", onClick: () => handleSelectUser(suggestion), children: [_jsx("div", { className: "w-8 h-8 rounded-full bg-blue-100 text-center flex items-center justify-center", children: suggestion.user_name &&
                                suggestion.user_name.charAt(0).toUpperCase() }), _jsxs("div", { className: "flex flex-col", children: [_jsx("span", { className: "text-sm font-medium text-[#344054]", children: suggestion.user_name }), _jsx("span", { className: "text-xs text-[#475467]", children: suggestion.email })] })] }, suggestion._id))) })), isFocused &&
                filteredSuggestions.length === 0 &&
                inputValue?.trim() !== "" && (_jsx("div", { ref: dropdownRef, className: `absolute z-10 mt-3 w-full bg-[#F8FAFC] border border-[#d1d5db] rounded-md shadow-lg max-h-60 overflow-auto`, children: _jsxs("div", { className: "flex gap-x-3 p-2 hover:bg-gray-100 justify-between", children: [_jsx(Info, { size: 26, className: "text-[#475467]" }), _jsxs("div", { className: "flex flex-col space-y-1", children: [_jsx("span", { className: "text-sm font-semibold", children: "User not found" }), _jsx("span", { className: "text-sm text-[#475467]", children: `It seems ${inputValue} hasn't joined Creative Workspace yet. Invite them now to start collaborating!` })] }), _jsx(Button, { label: "Invite", className: "px-[6px] py-[10px] bg-[#5928ED] hover:bg-[#4920CC] text-white w-[70px] h-8 rounded-md", onClick: (e) => {
                                e.stopPropagation();
                                handleInvite();
                            } })] }) }))] }));
};
const ShareProjectDialog = ({ visible, onHide, selectedProjectToShare, parentRef, }) => {
    const { user } = useAuthStore();
    const showToast = useToastStore((state) => state.showToast);
    // State for existing users with access
    const [usersWithAccess, setUsersWithAccess] = useState([]);
    // State for tracking users whose permissions have been modified
    const [modifiedUsers, setModifiedUsers] = useState({});
    // State for users to be newly added
    const [selectedUsers, setSelectedUsers] = useState([]);
    // UI State
    const [linkCopied, setLinkCopied] = useState(false);
    const [notifyPeople, setNotifyPeople] = useState(true);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedRole, setSelectedRole] = useState("viewer");
    // Mode state to track if we're adding new users or just modifying existing permissions
    const [isAddingNewUsers, setIsAddingNewUsers] = useState(false);
    // Add new state for general access
    const [generalAccess, setGeneralAccess] = useState("restricted");
    const [generalAccessRole, setGeneralAccessRole] = useState("viewer");
    const [isGeneralAccessModified, setIsGeneralAccessModified] = useState(false);
    // Reset state when dialog opens or closes
    useEffect(() => {
        if (visible) {
            fetchInitialUsersData();
            setLinkCopied(false);
        }
        else {
            resetAllState();
        }
    }, [visible]);
    // Effect to monitor selected users state and set mode accordingly
    useEffect(() => {
        if (selectedUsers.length > 0) {
            setIsAddingNewUsers(true);
            // If we have modified users and now we're adding new users,
            // we should auto-trigger save for the modified users
            if (Object.keys(modifiedUsers).length > 0) {
                handleSave();
            }
        }
        else {
            setIsAddingNewUsers(false);
        }
    }, [selectedUsers]);
    const resetAllState = () => {
        setSelectedUsers([]);
        setModifiedUsers({});
        setIsAddingNewUsers(false);
    };
    const fetchInitialUsersData = async () => {
        setIsLoading(true);
        try {
            // Call both APIs simultaneously using Promise.allSettled
            const results = await Promise.allSettled([
                listCWUsers(),
                listUsersByProject(selectedProjectToShare?._id || ""),
            ]);
            // Handle first API result (listCWUsers)
            if (results[0].status === "fulfilled") {
                const { responseData, error } = results[0].value;
                if (error) {
                    console.error("Error fetching available users:", error);
                    showToast("error", "Error", error.errorMessage);
                }
                else if (responseData?.users) {
                    setAvailableUsers(responseData.users);
                }
            }
            else {
                console.error("Failed to fetch available users:", results[0].reason);
            }
            // Handle second API result (listUsersByProject)
            if (results[1].status === "fulfilled") {
                const { responseData, error } = results[1].value;
                if (error) {
                    console.error("Error fetching project users:", error);
                    showToast("error", "Error", error.errorMessage);
                    // setting owner data manually
                    // console.log("setting owner data manually");
                    // setUsersWithAccess([
                    //   {
                    //     name: user?.provider?.name as string,
                    //     email: user?.email as string,
                    //     role: "owner",
                    //   },
                    // ]);
                }
                else if (responseData?.users) {
                    // Convert ProjectUser[] format to UserAccess[] format
                    console.log("printing project users", responseData.users);
                    const projectUsers = responseData.users
                        .filter((user) => user.user_email !== "allusers@techolution.com")
                        .map((user) => ({
                        name: user.user_name || user.user_email, // Use name if available, otherwise use email
                        email: user.user_email,
                        role: user.role, // Cast role to expected type
                    }));
                    const allowedToAllUsers = responseData.users.find((user) => user.user_email === "allusers@techolution.com");
                    if (allowedToAllUsers) {
                        setGeneralAccess("anyone");
                        setGeneralAccessRole(allowedToAllUsers.role);
                    }
                    setUsersWithAccess(projectUsers);
                }
            }
            else {
                console.error("Failed to fetch project users:", results[1].reason);
                showToast("error", "Error", results[1].reason);
                // setting owner data manually
                // console.log("setting owner data manually");
                // setUsersWithAccess([
                //   {
                //     name: user?.provider?.name as string,
                //     email: user?.email as string,
                //     role: "owner",
                //   },
                // ]);
            }
        }
        catch (error) {
            console.error("Error in fetchInitialUsersData:", error);
            showToast("error", "Error", "An error has occurred while fetching data");
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleCopyLink = () => {
        // Generate and copy a sharing link
        const link = window.location.origin + `/chat?projectId=${selectedProjectToShare?._id}`;
        console.log("printing link", link);
        navigator.clipboard.writeText(link);
        setLinkCopied(true);
        // Reset the copied state after 2.5 seconds
        setTimeout(() => {
            setLinkCopied(false);
        }, 2500);
    };
    const handleSave = async () => {
        try {
            console.log("clicked on handleSave");
            // Process modified user permissions
            const hasModifications = Object.keys(modifiedUsers).length > 0;
            setIsSaving(true);
            if (hasModifications) {
                console.log("Saving modified permissions for users:", modifiedUsers);
                // In a real implementation, you would send these changes to your backend
                // Update the main users list with modified permissions
                const updatedUsers = usersWithAccess?.map((user) => {
                    if (modifiedUsers[user.email]) {
                        return modifiedUsers[user.email];
                    }
                    return user;
                });
                setUsersWithAccess(updatedUsers);
                // here api call
                // take those whose role is not owner
                const accessUpdates = updatedUsers
                    ?.filter((user) => user.role !== "owner")
                    ?.map((user) => ({
                    user_email: user.email,
                    scope: user.role,
                    user_name: user.name,
                }));
                if (!selectedProjectToShare) {
                    console.warn("No project to share");
                    return;
                }
                if (!accessUpdates.length) {
                    console.warn("No access updates found");
                    return;
                }
                const { responseData, error } = await updateAccess(selectedProjectToShare?._id, accessUpdates);
                if (error) {
                    console.error("Error updating access", error);
                    showToast("error", "Error", error.errorMessage);
                    return;
                }
                if (responseData) {
                    console.log("Access updated successfully", responseData);
                    showToast("success", "Success", "Access updated successfully");
                }
                setModifiedUsers({});
            }
            // Process general access updates if needed
            if (isGeneralAccessModified) {
                // Update the access immediately when changing role for "Anyone with the link"
                if (!selectedProjectToShare) {
                    console.warn("No project to share");
                    return;
                }
                const customAccessUpdates = [];
                customAccessUpdates.push({
                    user_email: "allusers@techolution.com",
                    scope: generalAccess === "anyone" ? generalAccessRole : "remove",
                    user_name: "General Access",
                });
                const { responseData, error } = await updateAccess(selectedProjectToShare?._id, customAccessUpdates);
                if (error) {
                    console.error("Error updating general access role:", error);
                    showToast("error", "Error", error.errorMessage);
                    return;
                }
                if (responseData) {
                    console.log("General access role updated successfully", responseData);
                    showToast("success", "Success", "Access updated successfully");
                }
                setIsGeneralAccessModified(false);
            }
            // Only close the dialog if no new users are being added
            // This prevents closing when we have both modified users and new users selected
            if (selectedUsers.length === 0) {
                console.log("Closing dialog after save - no new users");
                onHide();
            }
        }
        catch (error) {
            console.error("Unexpected error during access update:", error);
            showToast("error", "Error", "Error updating access");
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleSend = async () => {
        try {
            console.log("clicked on handleSend");
            setIsSaving(true);
            // Process the selected users to send invites
            if (selectedUsers.length > 0) {
                console.log("Sending invites to new users:", selectedUsers.map((user) => ({
                    ...user,
                    role: selectedRole,
                })));
                // Add the new users to the usersWithAccess list
                const newUsers = selectedUsers.map((user) => ({
                    name: user.name,
                    email: user.email,
                    role: selectedRole,
                }));
                console.log("newUsers", newUsers);
                setUsersWithAccess([...usersWithAccess, ...newUsers]);
                const accessUpdates = newUsers?.map((user) => ({
                    user_email: user.email,
                    scope: user.role,
                    user_name: user.name,
                }));
                if (!selectedProjectToShare) {
                    console.warn("No project to share");
                    return;
                }
                if (!accessUpdates.length) {
                    console.warn("No access updates found");
                    return;
                }
                const { responseData, error } = await updateAccess(selectedProjectToShare?._id, accessUpdates);
                if (error) {
                    console.error("Error updating access", error);
                    showToast("error", "Error", error.errorMessage);
                    return;
                }
                if (responseData) {
                    console.log("Access updated successfully", responseData);
                    showToast("success", "Success", "Access updated successfully");
                }
                if (notifyPeople) {
                    const ownerUser = usersWithAccess.find((user) => user.role === "owner");
                    if (!ownerUser) {
                        console.error("Owner user not found for this project", selectedProjectToShare);
                        showToast("error", "Error", "Owner user not found for this project");
                        return;
                    }
                    if (!user ||
                        !user.email ||
                        !user.provider.name ||
                        !user.provider.access_token ||
                        !user.provider.refresh_token) {
                        showToast("error", "Error", "User details insufficient to notify people");
                        console.warn("User details insufficient to notify people", user);
                        return;
                    }
                    const projectURL = window.location.origin + `/chat?projectId=${selectedProjectToShare?._id}`;
                    const emailBodyForSharedProject = createFolderSharingTemplate(ownerUser?.name, selectedProjectToShare.name, projectURL);
                    const emailSubject = "Project Shared with you";
                    const hasEmailSendScope = checkModeSpecificScopes(user.provider.scope, emailSendScope);
                    if (!hasEmailSendScope) {
                        showToast("warn", "Warn", "Cant notify people, user does not have email send scope");
                        console.warn("Cant notify people, user does not have email send scope", user);
                        return;
                    }
                    // Send email notifications to all users who received access
                    try {
                        const emailPromises = accessUpdates.map(async (accessUpdate) => {
                            try {
                                const { responseData: emailResponseData, error: emailError } = await sendEmailToOwner(ownerUser.email, // fromEmail (owner's email)
                                accessUpdate.user_email, // toEmail (recipient's email)
                                emailBodyForSharedProject, // email body
                                emailSubject, // email subject
                                user?.provider?.access_token, user?.provider?.refresh_token);
                                if (emailError) {
                                    console.error(`Error sending email to ${accessUpdate.user_email}:`, emailError);
                                    return {
                                        status: 'rejected',
                                        reason: emailError,
                                        email: accessUpdate.user_email
                                    };
                                }
                                console.log(`Email sent successfully to ${accessUpdate.user_email}`);
                                return {
                                    status: 'fulfilled',
                                    value: emailResponseData,
                                    email: accessUpdate.user_email
                                };
                            }
                            catch (err) {
                                console.error(`Exception sending email to ${accessUpdate.user_email}:`, err);
                                return {
                                    status: 'rejected',
                                    reason: err,
                                    email: accessUpdate.user_email
                                };
                            }
                        });
                        // Execute all email sending promises in parallel
                        const emailResults = await Promise.allSettled(emailPromises);
                        // Log results
                        const successfulEmails = emailResults.filter(result => result.status === 'fulfilled').length;
                        const failedEmails = emailResults.filter(result => result.status === 'rejected').length;
                        console.log(`Email notifications: ${successfulEmails} sent successfully, ${failedEmails} failed`);
                        if (failedEmails > 0) {
                            showToast("warn", "Warn", `${successfulEmails} sent, ${failedEmails} failed.`);
                        }
                        else if (successfulEmails > 0) {
                            showToast("success", "Success", "All notifications sent successfully");
                        }
                    }
                    catch (emailErr) {
                        console.error("Error sending email notifications:", emailErr);
                        showToast("error", "Error", "Failed to send some email notifications");
                    }
                }
                setSelectedUsers([]);
                resetAllState();
            }
            // Close the dialog after sending
            onHide();
        }
        catch (error) {
            console.error("Unexpected error during access update:", error);
            showToast("error", "Error", "Error in granting access");
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleCancel = () => {
        console.log("clicked on handleCancel");
        if (isAddingNewUsers) {
            console.log("Cancel adding new users");
            // Cancel adding new users, but keep any modified permissions
            setSelectedUsers([]);
            setIsAddingNewUsers(false);
        }
        else {
            console.log("Cancel all changes");
            // Cancel all changes
            resetAllState();
            onHide();
        }
    };
    const handleAddUser = (user) => {
        // Ensure no duplicates
        if (!selectedUsers.some((u) => u.id === user.id)) {
            setSelectedUsers([...selectedUsers, user]);
        }
    };
    const handleRemoveUser = (userId) => {
        setSelectedUsers(selectedUsers.filter((user) => user.id !== userId));
    };
    const selectedRoleTemplate = (option) => {
        return (_jsx("div", { className: "text-sm text-center flex justify-center items-center", children: option.label }));
    };
    // 5. Update the itemTemplate for the dropdown to handle "Revoked" state
    const roleTemplate = (option) => {
        return (_jsx("div", { className: "text-sm text-center flex justify-center items-center", children: option.label }));
    };
    const handleRoleChange = (userEmail, newRole) => {
        // Find the user to modify
        const userToModify = usersWithAccess.find((u) => u.email === userEmail);
        if (userToModify && userToModify.role !== newRole) {
            // Store the modified user in our tracking state using email as key
            setModifiedUsers((prev) => ({
                ...prev,
                [userEmail]: { ...userToModify, role: newRole },
            }));
        }
        else if (userToModify &&
            userToModify.role === newRole &&
            modifiedUsers[userEmail]) {
            // If role changed back to original, remove from modified tracking
            const updatedModified = { ...modifiedUsers };
            delete updatedModified[userEmail];
            setModifiedUsers(updatedModified);
        }
    };
    const renderUserList = () => {
        if (isAddingNewUsers)
            return null;
        return usersWithAccess.map((user) => {
            // Get the current effective role (from modified state if exists, otherwise original)
            const currentRole = modifiedUsers[user.email]?.role || user.role;
            const isRevoked = currentRole === "remove";
            return (_jsxs("div", { className: "flex items-center justify-between py-3", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "w-10 h-10 rounded-full bg-blue-100 text-center flex items-center justify-center", children: user.name.charAt(0).toUpperCase() }), _jsxs("div", { className: "flex flex-col", children: [_jsx("span", { className: `text-sm font-medium text-[#344054] ${isRevoked ? "line-through" : ""}`, children: user.name }), _jsx("span", { className: `text-xs text-[#475467] ${isRevoked ? "line-through" : ""}`, children: user.email })] })] }), _jsx("div", { className: "flex items-center justify-center text-center", children: user.role === "owner" ? (_jsx("span", { className: "text-sm italic text-[#475467]", children: "Owner" })) : (_jsx(Dropdown, { value: currentRole, options: roleOptionsForExisting, optionLabel: "label", className: "w-auto border border-[#D1D5DB] rounded-[6px] h-8 text-sm text-center flex justify-center items-center", panelClassName: "shadow-md border border-[#d1d5db] w-auto rounded-[8px] text-sm text-center", itemTemplate: roleTemplate, valueTemplate: selectedRoleTemplate, onChange: (e) => handleRoleChange(user.email, e.value), pt: {
                                item: { className: "h-8" },
                                itemGroup: { className: "h-8" },
                                list: { className: "py-0" },
                                wrapper: { className: "py-0" },
                            } })) })] }, user.email));
        });
    };
    const dialogHeader = (_jsxs("div", { className: "flex items-center justify-between w-full", children: [_jsxs("span", { className: "text-xl font-semibold text-[#344054]", children: ["Share \"", selectedProjectToShare?.name || "", "\""] }), _jsx(Button, { icon: _jsx(X, { size: 20 }), className: "p-2 rounded-full text-[#667085]", text: true, onClick: onHide })] }));
    const renderDialogFooter = () => {
        // CASE 1: No new users, only modifying existing access
        if (!isAddingNewUsers) {
            const hasModifications = Object.keys(modifiedUsers).length > 0;
            return (_jsxs("div", { className: "flex justify-between w-full border-t pt-3", children: [_jsx(Button, { icon: linkCopied ? (_jsx(Check, { color: "#5928ED", size: 18 })) : (_jsx(Link, { color: "#5928ED", size: 18 })), label: linkCopied ? "Link copied" : "Copy link", className: "border border-[#5928ED] px-4 py-2 rounded-[8px] gap-x-2", text: true, onClick: handleCopyLink }), _jsx(Button, { label: isSaving ? "Saving..." : hasModifications ? "Save" : "Done", className: hasModifications
                            ? "py-2 px-8 bg-[#5928ED] hover:bg-[#4920CC] text-white rounded-[8px]"
                            : "py-2 px-8 bg-[#5928ED] hover:bg-[#4920CC] text-white rounded-[8px]", onClick: handleSave, disabled: isSaving, loading: isSaving })] }));
        }
        // CASE 2: Adding new users
        return (_jsxs("div", { className: "flex justify-between w-full border-t pt-3", children: [_jsx(Button, { icon: linkCopied ? (_jsx(Check, { color: "#5928ED", size: 18 })) : (_jsx(Link, { color: "#5928ED", size: 18 })), label: linkCopied ? "Link copied" : "Copy link", className: "border border-[#5928ED] px-4 py-2 rounded-[8px] gap-x-2", text: true, onClick: handleCopyLink }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { label: "Cancel", className: "border border-[#D1D5DB] px-4 py-2 rounded-[8px] text-[#374151]", disabled: isSaving, onClick: handleCancel }), _jsx(Button, { label: "Send", className: selectedUsers.length > 0
                                ? "py-2 px-8 bg-[#5928ED] hover:bg-[#4920CC] text-white rounded-[8px]"
                                : "py-2 px-8 bg-gray-300 text-gray-500 rounded-[8px]", disabled: selectedUsers.length === 0 || isSaving, onClick: handleSend })] })] }));
    };
    // Add this new function to handle general access changes
    const handleGeneralAccessChange = async (value) => {
        setGeneralAccess(value);
        setIsGeneralAccessModified(true);
    };
    // Add this function to handle general access role changes
    const handleGeneralAccessRoleChange = async (role) => {
        setGeneralAccessRole(role);
        setIsGeneralAccessModified(true);
    };
    // Add this function to generate selected access template
    const selectedGeneralAccessTemplate = (option) => {
        return (_jsx("div", { className: "text-sm flex items-center gap-x-2", children: option.value === "restricted" ? (_jsxs("span", { className: "flex items-center gap-x-2", children: [_jsx("span", { className: "p-1 rounded-full bg-gray-100", children: _jsx(LockKeyhole, { size: 20, color: "#344054" }) }), _jsxs("div", { className: " flex flex-col w-full", children: [option.label, _jsx("span", { className: "text-xs text-gray-500", children: option.description })] })] })) : (_jsxs("span", { className: "flex items-center gap-x-2", children: [_jsx("span", { className: "p-1 rounded-full bg-green-100", children: _jsx(Globe, { size: 20, color: "#027A48" }) }), _jsxs("div", { className: " flex flex-col w-full", children: [option.label, _jsx("span", { className: "text-xs text-gray-500", children: option.description })] })] })) }));
    };
    // Add this function to generate general access option template
    const generalAccessOptionTemplate = (option) => {
        return (_jsxs("div", { className: "flex items-center py-2 px-1", children: [_jsx("div", { className: "w-5 mr-2 flex-shrink-0 flex items-center justify-center", children: option.value === generalAccess && (_jsx(Check, { size: 16, color: "#5928ED" })) }), _jsx("span", { className: "text-sm font-medium", children: option.label })] }));
    };
    // Add this function to generate role template
    const generalAccessRoleTemplate = (option) => {
        return (_jsxs("div", { className: "flex items-center justify-between p-2", children: [_jsx("span", { className: "text-sm", children: option.label }), option.value === generalAccessRole && (_jsx(Check, { size: 16, className: " flex-shrink-0", color: "#5928ED" }))] }));
    };
    // Add this function to generate selected role template
    const selectedGeneralAccessRoleTemplate = (option) => {
        return (_jsx("div", { className: "text-sm text-center flex justify-center items-center", children: option.label }));
    };
    // Now, modify your renderUserList function to add the General Access section
    const renderGeneralAccessSection = () => {
        return (_jsxs("div", { className: "flex flex-col mt-4 w-full", children: [_jsx("h3", { className: "text-base font-medium text-[#344054] mb-2", children: "General access" }), _jsxs("div", { className: "flex items-center justify-between gap-x-2 bg-gray-50", children: [_jsx(Dropdown, { value: generalAccess, options: generalAccessOptions, onChange: (e) => handleGeneralAccessChange(e.value), optionLabel: "label", className: "min-w-[220px] w-full rounded-[6px] bg-gray-50", panelClassName: "shadow-md border border-[#d1d5db] rounded-[8px] mt-1", itemTemplate: generalAccessOptionTemplate, valueTemplate: selectedGeneralAccessTemplate }), generalAccess === "anyone" && (_jsx(Dropdown, { value: generalAccessRole, options: roleOptionsForNewUsers, onChange: (e) => handleGeneralAccessRoleChange(e.value), optionLabel: "label", className: "w-auto rounded-[6px] bg-gray-50 h-8 text-sm text-center flex justify-center items-center mr-1", panelClassName: "shadow-md w-auto rounded-[8px] text-sm text-center mt-1", itemTemplate: generalAccessRoleTemplate, valueTemplate: selectedGeneralAccessRoleTemplate, pt: {
                                item: { className: "h-8" },
                                itemGroup: { className: "h-8" },
                                list: { className: "py-0" },
                                wrapper: { className: "py-0" },
                            } }))] })] }));
    };
    // Loading skeleton for content
    const renderLoadingSkeleton = () => {
        return (_jsxs("div", { className: "flex flex-col gap-y-4", children: [_jsx(Skeleton, { height: "48px", className: "w-full mb-3" }), _jsxs("div", { className: "flex flex-col gap-y-2", children: [_jsx(Skeleton, { width: "150px", height: "24px", className: "mb-2" }), Array(3)
                            .fill(0)
                            .map((_, i) => (_jsxs("div", { className: "flex items-center justify-between py-3", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Skeleton, { shape: "circle", size: "40px" }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx(Skeleton, { width: "120px", height: "18px" }), _jsx(Skeleton, { width: "180px", height: "14px" })] })] }), _jsx(Skeleton, { width: "100px", height: "32px" })] }, i)))] })] }));
    };
    // Loading skeleton for footer
    const renderLoadingFooter = () => {
        return (_jsxs("div", { className: "flex justify-between w-full border-t pt-3", children: [_jsx(Skeleton, { width: "120px", height: "40px" }), _jsx(Skeleton, { width: "100px", height: "40px" })] }));
    };
    return (_jsx(Dialog, { header: dialogHeader, footer: isLoading ? renderLoadingFooter() : renderDialogFooter(), visible: visible, onHide: onHide, closable: false, draggable: false, dismissableMask: false, headerClassName: "rounded-t-[8px] text-base border-b pb-3", contentClassName: "rounded-b-[8px] pt-4 relative overflow-visible " // Added relative for dropdown positioning
        , appendTo: parentRef?.current, className: "max-w-[500px] w-[500px] font-content rounded-[8px] bg-[#fff] shadow-lg border", maskClassName: "!absolute !rounded-[8px] backdrop-blur-sm border border-[#d1d5db]", children: isLoading ? (renderLoadingSkeleton()) : (_jsxs("div", { className: "flex flex-col gap-y-4", children: [_jsxs("div", { className: "w-full flex items-center gap-2 relative", children: [" ", _jsx(SearchSelectChips, { selectedUsers: selectedUsers, onUserSelect: handleAddUser, onUserRemove: handleRemoveUser, suggestions: availableUsers, selectedProjectToShare: selectedProjectToShare, className: "flex-1", usersWithAccess: usersWithAccess }), selectedUsers.length > 0 && (_jsx(Dropdown, { value: selectedRole, options: roleOptionsForNewUsers, onChange: (e) => setSelectedRole(e.value), className: "min-w-[100px] border border-[#D1D5DB] rounded-[6px] text-sm", panelClassName: "shadow-md border border-[#d1d5db] rounded-[8px] mt-1 text-sm" }))] }), isAddingNewUsers && (_jsxs("div", { className: "flex items-center gap-2 my-2", children: [_jsx(Checkbox, { inputId: "notifyPeople", checked: notifyPeople, onChange: (e) => setNotifyPeople(e.checked || false), pt: {
                                root: {
                                    className: "border-[1.5px] border-gray-300 rounded overflow-hidden",
                                },
                                box: {
                                    className: "!hover:bg-transparent border-bottom border-gray-300 border-solid",
                                },
                                input: { className: "hover:bg-transparent" },
                            } }), _jsx("label", { htmlFor: "notifyPeople", className: "text-sm text-[#475467]", children: "Notify people" })] })), !isAddingNewUsers && (_jsxs("div", { className: "flex flex-col", children: [_jsx("h3", { className: "text-base font-medium text-[#344054] mb-2", children: "Users with access" }), _jsx("div", { className: "flex flex-col divide-y divide-[#EAECF0] max-h-[250px] overflow-y-auto pr-4 ", children: renderUserList() }), renderGeneralAccessSection()] }))] })) }));
};
export default ShareProjectDialog;
